import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useStore } from './main'

const StandardModals = [
    'accessInstruction',
    'awaitingControlRequestResponse',
    'awaitingControlTransferApplication',
    'awaitingTakeControlByForceApplication',
    'suspendBeforeExit',
    'bugTicket',
    'clipboardNotSupported',
    'manageGroupSession',
    'controlRequestRefused',
    'controlTransferredToUser',
    'enrollMachineInSupervisionRoom',
    'inactivityWarning',
    'usageInstructions',
    'iosFullscreenAdvices',
    'machineAlreadyInUse',
    'maintenanceMessage',
    'manageIso',
    'networkInfo',
    'noAdminInGroupSession',
    'noClipboardPermission',
    'respondToControlRequest',
    'sendRemoteCommand',
    'settings',
    'supervisionRoomEndApproaching',
    'touchDeviceError',
    'manageUsbDevices',
    'webcamConfig',
] as const

const ErrorModals = [
    'connectionFailure',
    'controlForciblyLost',
    'disconnectedForInactivity',
    'groupSessionHasEnded',
    'impossibleToConnect',
    'machineLockedInSupervisionRoom',
    'machineUnenrolledFromSupervisionRoom',
    'poweredOff',
    'sessionResumedOnAnotherDevice',
    'supervisedUserLeftSupervisionRoom',
    'supervisionRoomEnded',
    'userKickedFromGroupSession',
] as const

export type StandardModal = (typeof StandardModals)[number]
export type ErrorModal = (typeof ErrorModals)[number]
export type Modal = StandardModal | ErrorModal

export const useModalStore = defineStore('modal', () => {
    const current = ref<'' | Modal>('accessInstruction')
    const previous = ref<'' | Modal>('')

    const open = (type: Modal): void => {
        const mainStore = useStore()

        // If the modal is already opened, do nothing
        if (current.value === type) {
            return
        }

        // When an error modal is opened, it should prevent any other modal from being opened
        if (
            ErrorModals.includes(current.value as ErrorModal)
            && !ErrorModals.includes(type as ErrorModal)
        ) {
            return
        }

        // Save the current modal as previous if it exists
        current.value ? (previous.value = current.value) : (previous.value = '')

        current.value = type
        mainStore.disableCapture()
        mainStore.updateLockState()
    }

    const close = (type?: Modal): void => {
        const mainStore = useStore()

        if (type && type !== current.value) {
            return
        }

        current.value = ''
        mainStore.enableCapture()
        mainStore.updateLockState()
    }

    const is = (type: Modal): boolean => {
        return current.value === type
    }

    const reopenPrevious = (): void => {
        if (!previous.value) {
            return
        }

        const previousModal = previous.value
        previous.value = '' // Avoid infinite loops
        open(previousModal)
    }

    return { current, previous, open, close, is, reopenPrevious }
})
